import {
  PAINT_TONES,
  PAINT_CATEGORIES,
  type PaintTone,
  type PaintCategory,
} from '@pomoshnik/shared';

/**
 * Две chip-группы для каталога красок (Phase 3 Block 3 «Краски»).
 *
 * Группы:
 *   - **Тон** (9 chip'ов, multi-select) — фильтр PAINT_TONES.
 *   - **Категория** (2 chip'а, multi-select) — фильтр PAINT_CATEGORIES (Кухня/Шкаф).
 *     На старте Container выставляет default ['Кухня'] (Phase 3 интерпретация
 *     ADR-0010 §2 group-by-ralCode). Toggle на 'Шкаф' меняет видимое фото для
 *     тех же 213 RAL.
 *
 * Brand chip — отсутствует. RAL classic единственный бренд (PAINT_BRANDS.length=1).
 * Когда появится Tikkurila/Caparol — добавим chip.
 *
 * Презентационный компонент, использует свой inline `<ChipGroup>` хелпер
 * (паттерн `DecorFilterChips`). P-026 feature-isolation — не переиспользует
 * `decors-catalog/DecorFilterChips`, потому что у paint другой набор групп.
 */
export type PaintFilterField = 'tones' | 'categories';

export interface PaintFilterChipsProps {
  selectedTones: readonly PaintTone[];
  selectedCategories: readonly PaintCategory[];
  onToggle: (field: PaintFilterField, value: string) => void;
  onResetGroup: (field: PaintFilterField) => void;
}

export function PaintFilterChips(props: PaintFilterChipsProps) {
  return (
    <div className="flex flex-col gap-6">
      <ChipGroup
        groupLabel="Тон"
        values={PAINT_TONES}
        selected={props.selectedTones}
        onToggle={(v) => props.onToggle('tones', v)}
        onResetGroup={() => props.onResetGroup('tones')}
      />
      <ChipGroup
        groupLabel="Категория"
        values={PAINT_CATEGORIES}
        selected={props.selectedCategories}
        onToggle={(v) => props.onToggle('categories', v)}
        onResetGroup={() => props.onResetGroup('categories')}
      />
    </div>
  );
}

interface ChipGroupProps {
  groupLabel: string;
  values: readonly string[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  onResetGroup: () => void;
}

function ChipGroup({ groupLabel, values, selected, onToggle, onResetGroup }: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-display text-filter-label text-ink">{groupLabel}</span>
        <button
          type="button"
          onClick={onResetGroup}
          aria-label={`Сбросить выбор: ${groupLabel}`}
          className="bg-transparent text-muted underline text-base"
        >
          Сбросить
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        {values.map((value) => {
          const isSelected = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${groupLabel}: ${value}`}
              onClick={() => onToggle(value)}
              className={[
                'min-h-tap-chip-lg px-5 rounded-chip text-chip transition-colors',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
                isSelected
                  ? 'bg-chip-selected text-white border-[1.5px] border-chip-selected'
                  : 'bg-card text-ink border-[1.5px] border-accent active:opacity-90',
              ].join(' ')}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}
